import React, { useState, useEffect } from 'react'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import { useAuth } from "./api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import { HistoryDesktopDateRangePicker } from './HistoryDateRangePickers'

// Компонент чипа для статуса тикета
const StatusChip = ({ status }) => {
    const getChipClass = (status) => {
        switch (status) {
            case 'pending':
                return 'ticket-status-chip ticket-status-pending'
            case 'reject':
            case 'rejected':
                return 'ticket-status-chip ticket-status-rejected'
            case 'refund':
            case 'refunded':
            default:
                return 'ticket-status-chip ticket-status-refunded'
        }
    }

    const getStatusText = (status) => {
        switch (status) {
            case 'pending':
                return 'Pending'
            case 'reject':
            case 'rejected':
                return 'Rejected'
            case 'refund':
            case 'refunded':
            default:
                return 'Refunded'
        }
    }

    return (
        <span className={getChipClass(status)} style={{
            border: '1px solid currentColor',
            padding: '6px 12px',
            borderRadius: '7px',
            fontSize: 'var(--text-xs)',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
      justifyContent: 'center',
            width: '100px',
            height: '35px',
            cursor: 'default',
            position: 'relative',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {getStatusText(status)}
        </span>
    )
}

// Компонент пагинации для десктопа
const PaginationActions = ({ page, rowsPerPage, count, onPageChange }) => {
    const safePage = isNaN(page) ? 0 : page
    const safeRowsPerPage = isNaN(rowsPerPage) ? 10 : rowsPerPage
    const safeCount = isNaN(count) ? 0 : count
    
    const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
    
    const handleFirstPage = () => onPageChange(null, 0)
    const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1))
    const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1))
    const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1))

    return (
        <div style={{ display: 'flex', gap: '4px' }}>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage === 0 ? 0.5 : 1
                }}
                onClick={handleFirstPage}
                disabled={safePage === 0}
            >
                <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage === 0 ? 0.5 : 1
                }}
                onClick={handlePrevPage}
                disabled={safePage === 0}
            >
                <KeyboardArrowLeft style={{ fontSize: '14px' }} />
            </button>
            
            <span style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 var(--spacing-sm)',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center'
            }}>
                {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, safeCount)} of {safeCount}
            </span>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage >= totalPages - 1 ? 0.5 : 1
                }}
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1}
            >
                <KeyboardArrowRight style={{ fontSize: '14px' }} />
            </button>
            
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage >= totalPages - 1 ? 0.5 : 1
                }}
                onClick={handleLastPage}
                disabled={safePage >= totalPages - 1}
            >
                <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />
            </button>
        </div>
    )
}

const TicketsTable = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()

    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [ticketsList, setTicketsList] = React.useState([])
    const [totalTickets, setTotalTickets] = React.useState(0)
    const [selectedTicket, setSelectedTicket] = React.useState(0)

    const [startDate, setStartDate] = React.useState(() => dayjs())
    const [endDate, setEndDate] = React.useState(() => dayjs().endOf('month'))

    // CSS стили для толстой полосы прокрутки
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Адаптивная полоса прокрутки для таблицы TicketsTable */
            .tickets-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .tickets-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .tickets-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .tickets-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .tickets-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .tickets-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .tickets-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .tickets-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .tickets-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .tickets-table-container,
            :root:not([data-theme="light"]) .tickets-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - ticketsList?.length) : 0

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD')
            const dateEndFormated = endDate.format('YYYY-MM-DD')

            // Получаем токен один раз
            const token = await getAccessTokenSilently()

            const ticketsResponse = await props.apiClient.setToken(token).getUserTickets(dateStartFormated, dateEndFormated, page, rowsPerPage)
            setTicketsList(ticketsResponse?.refunds || [])
            setTotalTickets(ticketsResponse?.total || 0)
          } catch (e) {
          }
      }
      callAsync()   
    },[startDate, endDate, page, rowsPerPage, getAccessTokenSilently])

    const handleDateRangeChange = React.useCallback((start, end) => {
      setStartDate(start)
      setEndDate(end)
    }, [])

    const getTicketsDefaultRange = React.useCallback(() => ({
      start: dayjs().startOf('day'),
      end: dayjs().endOf('month')
    }), [])

    const rowClick = (row) => {
      // console.log(row.id)
      setSelectedTicket(row)
    }

    return (
        <div>
            <h1 style={{ 
                margin: '0 0 var(--spacing-lg) 0',
                fontWeight: 600,
                fontSize: 'var(--text-2xl)',
                background: 'var(--gradient-orange)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
            }}>
                Tickets
            </h1>
            
            {/* Фильтры по дате */}
            <div style={{
                background: 'var(--medium-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-lg)',
                paddingBottom: 'calc(var(--spacing-lg) - 37px)',
                marginBottom: 'calc(var(--spacing-lg) - 10px)',
                boxShadow: 'var(--shadow-card)',
                display: 'flex',
                gap: 'var(--spacing-lg)',
                alignItems: 'end'
            }}>
                <HistoryDesktopDateRangePicker
                    label="Date range"
                    startDate={startDate}
                    endDate={endDate}
                    onRangeChange={handleDateRangeChange}
                    getResetRange={getTicketsDefaultRange}
                />
            </div>
            
            {ticketsList?.length > 0 ? (
                <>
                    <div className="table-container tickets-table-container" style={{ overflowX: 'auto' }}>
                        <table className="table table-compact" style={{ minWidth: '1000px' }}>
                            <thead>
                                <tr>
                                    <th>Creation Date</th>
                                    <th>Status</th>
                                    <th>Proxy ID</th>
                                    <th>Refund Reason</th>
                                    <th>Description</th>
                                    <th>Close Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ticketsList?.map((row, index) => (
                                    <tr 
                                        key={index}
                                      onClick={() => rowClick(row)}
                                        style={{
                                            backgroundColor: selectedTicket?.refundid === row.refundid ? 'var(--dark-gray)' : 'transparent',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <td>{moment(row?.createdAt).format('L LT')}</td>
                                        <td>
                                            <StatusChip status={row?.status} />
                                        </td>
                                        <td>{row?.rental?.rentalid}</td>
                                        <td>{row?.reason}</td>
                                        <td>{row?.message}</td>
                                        <td>{row?.status === 'pending' ? '' : moment(row?.updatedAt).format('L LT')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 'var(--spacing-md)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        marginTop: 'var(--spacing-md)'
                    }}>
                        {/* Строка с количеством строк на странице */}
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 'var(--spacing-sm)' 
                        }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                Rows per page:
                            </span>
                            <select 
                                value={rowsPerPage}
                                onChange={handleChangeRowsPerPage}
                                style={{
                                    background: 'var(--dark-gray)',
                                    border: '1px solid var(--light-gray)',
                                    borderRadius: 'var(--radius-sm)',
                                    color: 'var(--text-light)',
                                    padding: '6px 8px',
                                    fontSize: 'var(--text-sm)',
                                    minWidth: '60px'
                                }}
                            >
                                {[10, 20, 30, 40, 50].map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Навигация */}
                        <PaginationActions 
                            page={page}
                            rowsPerPage={rowsPerPage}
                            count={totalTickets}
                            onPageChange={handleChangePage}
                        />
                    </div>
                </>
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">🎫</div>
                    <h3 className="empty-state-title">No tickets found</h3>
                    <p className="empty-state-description">
                        You don't have any tickets for the selected period.
                    </p>
            </div>
            )}
        </div>
    )
}

export default TicketsTable